import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, Save } from "lucide-react";
import { useNote, usePutNote } from "@/api/hooks";
import { errorMessage, isApiError } from "@/api/client";
import type { Note, StaleConflict } from "@/api/types";
import { isMac, timeAgo } from "@/lib/utils";
import { useToast } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { MarkdownEditor } from "@/components/MarkdownEditor";
import { OutlineRail, columnClass, frameClass, hasRail } from "@/components/OutlineRail";
import type { OutlineItem } from "@/lib/blocks";
import { PageSkeleton } from "@/components/ui/skeleton";
import { ErrorState } from "@/components/States";
import { ClientBadge } from "@/components/ClientBadge";

export default function NoteEditPage() {
  const { id = "" } = useParams<{ id: string }>();
  const note = useNote(id, { refetchOnMount: "always" });
  if (note.isLoading) return <PageSkeleton />;
  if (note.isError) return <ErrorState error={note.error} retry={() => note.refetch()} />;
  return <Editor key={note.data!.id} note={note.data!} />;
}

function Editor({ note }: { note: Note }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const put = usePutNote(note.id);
  const [text, setText] = useState(note.markdown);
  const [version, setVersion] = useState(note.version);
  const [conflict, setConflict] = useState<StaleConflict | null>(null);
  // Start from the saved outline so the frame matches the view the moment the editor opens.
  const [outline, setOutline] = useState<OutlineItem[]>(note.outline ?? []);
  const onParsed = useCallback((p: { outline: OutlineItem[] }) => setOutline(p.outline), []);
  const rail = hasRail(outline);
  const dirty = text !== note.markdown;

  useEffect(() => {
    if (!dirty) return;
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [dirty]);

  function save(overrideVersion?: number) {
    put.mutate(
      { version: overrideVersion ?? version, markdown: text },
      {
        onSuccess: (n) => {
          toast({ title: "Saved", description: `Version ${n.version}`, tone: "success" });
          navigate(`/notes/${n.id}`);
        },
        onError: (err) => {
          if (isApiError(err, 409) && err.body && typeof err.body === "object") {
            const b = err.body as Partial<StaleConflict>;
            setConflict({ error: b.error ?? "stale version", version: Number(b.version ?? 0), markdown: String(b.markdown ?? "") });
            return;
          }
          toast({ title: "Could not save", description: errorMessage(err), tone: "error" });
        },
      },
    );
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (!put.isPending) save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // re-bound each render so it sees the latest text/version

  return (
    <div className={frameClass(rail)}>
      <div className={columnClass(rail)}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <Link to={`/notes/${note.id}`} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> {note.title || "Untitled"}
          </Link>
          <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
            <span>Editing v{version}</span>
            <ClientBadge client={note.updatedByClient} />
            <span>updated {timeAgo(note.updatedAt)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to={`/notes/${note.id}`}>Cancel</Link>
          </Button>
          <Button size="sm" onClick={() => save()} disabled={put.isPending || !!conflict}>
            <Save /> {put.isPending ? "Saving…" : "Save"}
            <kbd className="ml-1 hidden rounded border border-primary-foreground/30 px-1 font-mono text-[10px] opacity-80 sm:inline">{isMac() ? "⌘" : "Ctrl"}S</kbd>
          </Button>
        </div>
      </div>

      {conflict && (
        <div className="mb-4 rounded-xl border border-tone-warning/50 bg-tone-warning/10 p-4 text-sm">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 size-4 shrink-0 text-tone-warning" />
            <div className="min-w-0 flex-1">
              <div className="font-medium">This note changed while you were editing</div>
              <p className="mt-1 text-muted-foreground">
                The server has version {conflict.version}; you started from version {version}. Reload to take the server's text (your edits are lost), or overwrite it with yours.
              </p>
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-muted-foreground">Show the server's current markdown</summary>
                <pre className="mt-2 max-h-64 overflow-auto rounded-lg border bg-card p-3 font-mono text-xs leading-relaxed">{conflict.markdown}</pre>
              </details>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setText(conflict.markdown);
                    setVersion(conflict.version);
                    setConflict(null);
                  }}
                >
                  Reload and lose my edits
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => {
                    setVersion(conflict.version);
                    setConflict(null);
                    save(conflict.version);
                  }}
                >
                  Overwrite with mine
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConflict(null)}>
                  Keep editing
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <MarkdownEditor value={text} onChange={setText} onParsed={onParsed} />
      </div>
      {rail && <OutlineRail outline={outline} links={false} />}
    </div>
  );
}
